import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PriceHero.css';

// Import all the images for the slides
import imageSlide1 from '../../assets/images/pricehero-devices.png';
import imageSlide2 from '../../assets/images/price-coverage-map.png';
import imageSlide3 from '../../assets/images/price-testimonial.png'; // This will act as a fallback/poster image

// Array containing the content for each slide
const slides = [
  {
    type: 'default',
    title: 'Price matters',
    description: 'Power your critical decisions with reliable price indexing. CRISHI provides you with independent pricing, data, and consulting that keeps you ahead in the commodities market.',
    image: imageSlide1,
    button: { text: 'Book a demo', link: '/signup' }
  },
  {
    type: 'default',
    title: 'Explore our global coverage',
    description: 'CRISHI now offers real-time prices and market data for key agricultural commodities including wheat, rice, maize, and soybeans.',
    image: imageSlide2,
    button: { text: 'View prices', link: '/prices' }
  },
  {
    type: 'video',
    title: 'How our clients improved pricing with CRISHI',
    description: 'By using CRISHI\'s real-time, market-verified price data, our clients have fast-tracked their pricing decisions, reduced procurement data processing time by 50%, and responded faster to market changes with confidence.',
    image: imageSlide3, 
    videoId: '2ZL-0qLlrtw?si=42x0AXaCcw-SJu7p', // --- ADD THE YOUTUBE VIDEO ID HERE ---
    button: { text: 'Explore more success stories', link: '/success-stories' }
  }
];

const PriceHero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef(null);
  const playerRef = useRef(null); // Ref to hold the YouTube player instance

  const startTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current); // Clear previous timer
    timeoutRef.current = setTimeout(
      () => setActiveIndex((prevIndex) => (prevIndex === slides.length - 1 ? 0 : prevIndex + 1)),
      8000
    );
  };

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  // This useEffect now ONLY handles the YouTube API player logic
  useEffect(() => {
    // Check if the current slide is a video slide
    if (slides[activeIndex].type === 'video') {
      const onPlayerStateChange = (event) => {
        if (event.data === window.YT.PlayerState.PLAYING) {
          resetTimeout(); // Pause slider
        } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
          startTimeout(); // Resume slider
        }
      };

      const createPlayer = () => {
        // Ensure player is destroyed before creating a new one to prevent conflicts
        if (playerRef.current) {
          playerRef.current.destroy();
        }
        playerRef.current = new window.YT.Player(`youtube-player-${activeIndex}`, {
          videoId: slides[activeIndex].videoId,
          events: {
            'onStateChange': onPlayerStateChange
          }
        });
      };

      // Check if the YT API script is loaded
      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        window.onYouTubeIframeAPIReady = createPlayer; // Create player when API is ready
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        createPlayer(); // If API is already loaded, just create the player
      }
    }
  }, [activeIndex]); // This effect depends only on the activeIndex

  // This useEffect ONLY handles the non-video slide timer
  useEffect(() => {
    if (slides[activeIndex].type !== 'video') {
      startTimeout();
    }
    return () => resetTimeout();
  }, [activeIndex]);

  const goToSlide = (slideIndex) => {
    setActiveIndex(slideIndex);
  };


  return (
    <div className="price-hero-slider">
      <div className="price-slides-wrapper" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
        {slides.map((slide, index) => (
          <div key={index} className={`price-slide ${slide.type === 'video' ? 'video-slide' : ''}`}>
            <div className="price-hero-content">
              <h1>{slide.title}</h1>
              <p>{slide.description}</p>
              <Link to={slide.button.link}>
                <button className="book-demo-btn">{slide.button.text}</button>
              </Link>
            </div>
            <div className="price-hero-graphic">
              {/* --- THIS IS THE KEY LOGIC CHANGE --- */}
              {slide.type === 'video' ? (
                <div className="video-player-container">
                  <iframe
                    // Add the 'key' prop to force re-render when slide changes
                    key={activeIndex} 
                    src={`https://www.youtube.com/embed/${slide.videoId}`}
                    title="Client Testimonial Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <img src={slide.image} alt={slide.title} />
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="pagination-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${activeIndex === index ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          ></button>
        ))}
      </div>
    </div>
  );
};

export default PriceHero;